import React from 'react';
import { Separator } from '@/components/ui/separator';
import { formatDateTime12, formatDateTime24 } from '@/utils/helpers';

export type AlertTriageListRow = {
  reading?: string;
  device?: string;
  timestamp?: string;
};

interface PulseOximeterAlertViewProps {
  data: any;
  listRow?: AlertTriageListRow | null;
}

const nonEmpty = (v: unknown) =>
  v != null && String(v).trim() !== "" ? String(v).trim() : "";

const PulseOximeterAlertView: React.FC<PulseOximeterAlertViewProps> = ({ data, listRow }) => {
  const pulseOximeterData = data?.data?.forms?.[0]?.pulseOximeterData;
  if (!pulseOximeterData) return null;

  const questions: Array<{ question: string; answer: string | number | null | undefined; details?: string }> = [];


  function CheckFlowType(pulseData : any) : string {
   
    let type : string = "";
    if(pulseData?.seletedPath ===  "repirateoryPath"){
         let subPathOption : string = pulseData?.respiratoryFollowUps?.find((que : any) => que?.question === "An oxygen level of <90%?")?.answer; 
         if(subPathOption === "yes"){
              type = "path1";
         }else{
              type = "path2";
         }
    }
    else{
      // Chage needed
        let subPathOption : string = pulseData?.respiratoryFollowUps?.find((que : any) => que?.question === "A heart rate <50 BPM?")?.answer; 
         if(subPathOption === "yes"){
              type = "path3";
         }else{
              type = "path4";
         }
    }

    return type;
  }

  const flowType : string = CheckFlowType(pulseOximeterData as any);
  console.log("flowType",flowType);

  // Selected Path Options
  const selectedPathOptions = pulseOximeterData?.seletedPathOptions?.join(', ');
  if (selectedPathOptions) {
    questions.push({ 
      question: 'Selected Path Options', 
      answer: selectedPathOptions 
    });
  }

    if(flowType == "path3" || flowType == "path4"){
    // Respiratory Follow-ups
    questions.push({ 
      question: 'Currently Experiencing ', 
      answer:  pulseOximeterData?.patientExperiencing 
    });
  }

  // Respiratory Follow-ups
  const respiratoryFollowUps = pulseOximeterData?.respiratoryFollowUps;
  if (Array.isArray(respiratoryFollowUps) && respiratoryFollowUps.length > 0) {
    respiratoryFollowUps.forEach((item: any) => {
      questions.push({
        question: item.question || '',
        answer: item.answer || '',
        details: item.details || undefined
      });
    });
  }

  // Heart rate and oxygen level questions
  const heartRateQuestion = pulseOximeterData?.['A heart rate <50 BPM?'];
  const oxygenLevelQuestion = pulseOximeterData?.['An oxygen level of <90%?'];

  if (heartRateQuestion) {
    questions.push({ 
      question: 'A heart rate <50 BPM?', 
      answer: heartRateQuestion 
    });
  }

  if (oxygenLevelQuestion) {
    questions.push({ 
      question: 'An oxygen level of <90%?', 
      answer: oxygenLevelQuestion 
    });
  }

   // Recommendations -> path-2
  const recommendations = pulseOximeterData?.recommendations?.join(', ');
  if (recommendations) {
    questions.push({ 
      question: 'Recommendations', 
      answer: recommendations 
    });
  }

    // Currently Experiencing -> path1
  const currentlyExperiencing = pulseOximeterData?.currenlyExperiencing;
  if (Array.isArray(currentlyExperiencing)) {
    const selectedSymptoms = currentlyExperiencing
      .filter((item: any) => item?.selected === 'yes')
      .map((item: any) => item?.option)
      .filter(Boolean);

    if (selectedSymptoms.length > 0) {
      questions.push({ 
        question: 'Currently Experiencing', 
        answer: selectedSymptoms.join(', ') 
      });
    }
  }

  if (questions.length === 0) return null;

  const formData = data?.data?.forms?.[0];
  const patient = formData?.patientId;
  const patientName = patient ? [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(' ') : '';
  const timestampRaw =
    formData?.createdAt ?? data?.data?.createdAt ?? data?.data?.triageTime;
  const timestamp = timestampRaw ? formatDateTime12(timestampRaw) : '';
  const timestampSafe = nonEmpty(listRow?.timestamp) || timestamp || "—";
  const deviceTypeRaw = formData?.deviceType ?? data?.data?.deviceType ?? '';
  const deviceDisplay = deviceTypeRaw
    ? deviceTypeRaw
        .replace(/_/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    : '';
  const deviceDisplaySafe = nonEmpty(listRow?.device) || deviceDisplay || "—";

  const readingValues = formData?.readingValues ?? data?.data?.readingValues ?? {};
  const readingValuesFormatted =
    readingValues && typeof readingValues === 'object' && !Array.isArray(readingValues)
      ? Object.entries(readingValues)
          .map(([key, value]) => {
            const formattedKey =
              key && typeof key === 'string'
                ? key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
                : String(key);
            return `${formattedKey}: ${value}`;
          })
          .join(', ')
      : '';
  const readingId = formData?.readingId ?? data?.data?.readingId;
  const readingFromId =
    typeof readingId === 'object' && readingId !== null
      ? readingId?.readingId ?? readingId?._id ?? ''
      : readingId ?? '';
  const readingDisplay = readingValuesFormatted || (readingFromId ? String(readingFromId) : '');
  const readingDisplaySafe =
    nonEmpty(listRow?.reading) || readingDisplay || "—";
  const submittedBy = formData?.submittedBy;
  const submittedByName = submittedBy?.name || '';

  return (
    <div className="bg-gray-100 min-h-screen py-8 px-4">
      <div className="bg-white shadow-lg" style={{ 
        width: '595px', 
        maxWidth: '100%', 
        margin: '0 auto', 
        boxSizing: 'border-box',
        padding: '40px',
        minHeight: '842px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}>
        <div className="header mb-8 bg-[#eeeeee] p-5 rounded-md">
          <h2 className="font-medium teachers mb-4 text-lg">
            Triage View
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {patientName && (
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">PATIENT NAME:</span>
                <span className="ml-2 text-black text-sm teachers">{patientName}</span>
              </div>
            )}
            {submittedByName && (
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">SUBMITTED BY:</span>
                <span className="ml-2 text-black text-sm teachers">{submittedByName}</span>
              </div>
            )}
          </div>
          {submittedByName && (
            <div className="mt-4 flex flex-col gap-2">
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">READING:</span>
                <span className="ml-2 text-black text-sm teachers">{readingDisplaySafe}</span>
              </div>
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">DEVICE:</span>
                <span className="ml-2 text-black text-sm teachers">{deviceDisplaySafe}</span>
              </div>
              <div>
                <span className="font-semibold text-[#6E6E6E] teachers text-sm">TIMESTAMP:</span>
                <span className="ml-2 text-black text-sm teachers">{timestampSafe}</span>
              </div>
            </div>
          )}
        </div>
        <h3 className="font-medium teachers mb-6 text-base">
          Pulse Oximeter Triage Form
        </h3>
        <div className="space-y-0">
          {questions.map((item, index) => (
            <div key={index} className="mb-4">
              <div className="teachers text-xs mb-2.5">{item.question}</div>
              {item.answer && (
                <div className="font-medium teachers text-xs">
                  {item.answer}
                </div>
              )}
              {item.details && (
                <div className="font-medium teachers text-xs mt-2">
                  {item.details}
                </div>
              )}
              <Separator className='bg-[#dddddd] my-2'/>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PulseOximeterAlertView;
